import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getRoomType } from '@/service/dashboard';
import * as R from 'ramda';
// ==================== 初始状态 ====================

const initialState = {
  fetching: false,
  error: null,
  query: {},
  data: [], // 原始数据
  // 不同服务类型的数据存储
  default: [],
}

// ==================== 字段适配配置 ====================

/**
 * 字段适配配置表 - 处理不同API返回的字段名差异
 * 解决后端接口字段不一致的问题，提供统一的前端数据结构
 */
const keyConfig = [
  { key: 'default', id: 'room_type_id', name: 'room_type_name', fullName: 'room_type_full_name', description: 'room_type_description', price: 'room_type_price', status: 'room_type_status' },
  // 预留其他API字段映射
  // { key: 'featured', id: 'id', name: 'name', fullName: 'full_name' }
];

const defaultKey = { id: 'room_type_id', name: 'room_type_name', fullName: 'room_type_full_name', description: 'room_type_description',price: 'room_type_price', status: 'room_type_status'
};

// ==================== 服务类型映射配置 ====================

/**
 * 服务类型映射函数 - 根据serviceType调用不同的API服务
 * 实现灵活的多服务支持，便于扩展新的API接口
 * @param {string} serviceType - 服务类型标识
 * @param {Object} query - 查询参数
 * @returns {Function} 返回对应的API调用函数
 */
const switchPayload = (serviceType, query) => {
  const payload = {
    default: () => getRoomType(query),
    // 预留其他服务类型扩展点
    // featured: () => getFeaturedRoomTypes(query),
    // nearby: () => getNearbyRoomTypes(query),
    undefined: () => console.error('Redux: fetchRoomType 请求未定义 serviceType 类型')
  };
  return payload[serviceType] || payload.default;
};

// ==================== 数据处理函数 ====================

/**
 * 数据格式化函数 - 根据serviceType和dataType处理数据
 * 支持多种数据格式处理，满足不同业务场景需求
 * @param {Array} data - 原始数据数组
 * @param {string} serviceType - 服务类型
 * @param {string} dataType - 数据类型，支持逗号分隔的多种类型
 * @param {Object} query - 查询参数对象
 * @returns {Object} 处理后的数据对象，键为数据类型，值为对应的数据数组
 */
const formatData = ({ data, serviceType, dataType = 'default', query = {} }) => {
  if (!Array.isArray(data)) return data;

  // 获取当前服务类型的字段映射
  const fieldMap = keyConfig.find(kc => kc.key === serviceType) || defaultKey;

  // 统一处理所有数据类型（单个或多个）
  const dataTypes = dataType.split(',').map(type => type.trim());
  const result = {};

  dataTypes.forEach(type => {
    result[type] = processSingleDataType({
      data,
      fieldMap,
      dataType: type,
      query
    });
  });

  return result;
};

/**
 * 处理单一数据类型的函数
 * @param {Object} params - 参数对象
 * @param {Array} params.data - 原始数据数组
 * @param {Object} params.fieldMap - 字段映射配置
 * @param {string} params.dataType - 单一数据类型
 * @param {Object} params.query - 查询参数对象
 * @returns {Array} 处理后的数据数组
 */
const processSingleDataType = ({ data, fieldMap, dataType, query }) => {
  
  switch (dataType) {

    // 分组数据：用于树形结构等场景
    case 'groupByData': {
      const groupByData = R.groupBy(v => v[query.groupByKey], data);
      const dataListKeys = Object.keys(groupByData);

      // 处理每个分组的数据，增加图片处理逻辑
      const processedGroupByData = {};
      
      dataListKeys.forEach(key => {
        const groupData = groupByData[key];
        processedGroupByData[key] = groupData.map(item => {
          const processedItem = { ...item };
          
          // 1. 检查picture字段是否有值
          if (item.picture && item.picture.trim()) {
            // 2. 按逗号分割picture
            const pictureArray = item.picture.split(',').map(p => p.trim()).filter(p => p);
            
            if (pictureArray.length > 0) {
              // 第一个作为defaultImg: url + 'T' + picture[0]
              processedItem.defaultImg = `${item.url}T${pictureArray[0]}`;
              
              // 第二个及以后作为imglist，遍历picture数组，重制每一个url
              if (pictureArray.length > 1) {
                processedItem.imglist = pictureArray.map(pic => ({
                  url: `${item.url}T${pic}`,
                  name: pic
                }));
              } else {
                processedItem.imglist = [];
              }
            }
          }
          
          return processedItem;
        });
      });

      return processedGroupByData
    }
    case 'videoList': {
      const filterData = data
        .filter(item => item.video)
        .map(m => {
          // 2. 按逗号分割picture
          const pictureArray = m.picture.split(',').map(p => p.trim()).filter(p => p);
          return {
            ...m,
            defaultImg:`${m.url}T${pictureArray[0]}`
          }
        });
      return filterData
    }

    case 'url720': {
      const firstData = data
        .filter(item => item.pic360)
        .map(m => {
          const pictureArray = m.picture.split(',')
          return {
            ...m,
            defaultImg: `${m.url}T${pictureArray[0]}`
          }
        });
      return firstData;
    }

    // 默认处理：保持原始数据结构
    default:
      return data
  }
};

// ==================== 异步Action ====================

/**
 * 异步获取房间类型列表的thunk action
 * 支持多服务类型和多种数据格式处理
 * @param {Object} options - 请求选项
 * @param {string} options.serviceType - 服务类型，默认为'default'
 * @param {string} options.dataType - 数据类型，控制格式化逻辑，默认为'default'
 * @param {Object} options.query - 查询参数对象
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchRoomType = createAsyncThunk(
  'roomType/fetchRoomType',
  async ({ serviceType = 'default', dataType = 'default', query = {} }, { rejectWithValue }) => {
    try {
      const response = await switchPayload(serviceType, query)();
      return {
        data: response.data,
        serviceType,
        dataType,
        query
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取房间类型列表失败');
    }
  }
);

// ==================== Redux Slice ====================

/**
 * 房间类型Redux slice
 * 管理房间类型的状态和操作，支持多服务类型数据存储
 */
const roomTypeSlice = createSlice({
  name: 'roomType',
  initialState,
  reducers: {
    /**
     * 重置房间类型状态
     * @param {Object} state - 当前状态
     * @param {Object} action - action对象，payload可包含serviceType指定重置的服务类型
     */
    resetRoomTypeState: (state, action) => {
      const { serviceType } = action.payload || {};
      if (!serviceType) {
        // 重置所有状态
        Object.keys(state).forEach(key => {
          if (Array.isArray(state[key])) {
            state[key] = [];
          }
        });
        state.query = {};
        state.groupByData = {};
      } else {
        // 重置指定服务类型的数据
        state[serviceType] = [];
        state.query[serviceType] = {};
      }
    },

    /**
     * 更新查询参数
     * @param {Object} state - 当前状态
     * @param {Object} action - action对象，payload包含serviceType和query
     */
    setQuery: (state, action) => {
      const { serviceType = 'default', query } = action.payload;
      state.query[serviceType] = { ...state.query[serviceType], ...query };
    },


    /**
     * 更新房间类型数据
     * @param {Object} state - 当前状态
     * @param {Object} action - action对象，payload包含serviceType和查询参数
     */
    updateRoomTypeData: (state, action) => {
      if (!state || !action.payload) return state;
      const { dataType, serviceType = 'default', query = {} } = action.payload;

      // 获取当前服务类型的字段映射
      const fieldMap = keyConfig.find(kc => kc.key === serviceType) || defaultKey;
      
      // 处理数据
      const processedData = processSingleDataType({
        data: state.data,
        fieldMap,
        dataType,
        query
      });

      // 更新数据
      state[`${serviceType}_${dataType}`] = processedData;
      state.query[serviceType] = query;

      return state;
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理请求开始状态
      .addCase(fetchRoomType.pending, (state, action) => {
        const arg = action.meta.arg || {};
        const { serviceType = 'default', query = {} } = arg;
        state.fetching = true;
        state.error = null;
        state.query[serviceType] = query;
        state[serviceType] = [];
      })
      // 处理请求成功状态
      .addCase(fetchRoomType.fulfilled, (state, action) => {
        const { data, serviceType, dataType, query } = action.payload;
        state.fetching = false;
        state.error = null;
        state.data = data; // 记录原始数据

        // 先初始化所有相关的状态字段
        const dataTypes = dataType.split(',').map(type => type.trim());
        
        // 根据serviceType和dataType格式化数据，传入query参数
        const formattedData = formatData({ data, serviceType, dataType, query });

        // 更新所有数据类型对应的状态字段
        dataTypes.forEach(type => {
          const key = `${serviceType}_${type}`;
          state[key] = formattedData[type] || [];
        });

      })
      // 处理请求失败状态
      .addCase(fetchRoomType.rejected, (state, action) => {
        const arg = action.meta.arg || {};
        const { serviceType = 'default' } = arg;
        state.fetching = false;
        state.error = action.payload;
        state[serviceType] = [];
        // 确保即使出错也不阻塞页面其他内容
        console.error('fetchRoomType 请求失败:', action.payload);
      });
  }
});

// ==================== 导出Action Creators ====================

// 导出所有同步action creators
export const { 
  resetRoomTypeState, 
  setQuery, 
  updateRoomTypeData 
} = roomTypeSlice.actions;

// ==================== 选择器函数集合 ====================

/**
 * 房间类型数据选择器集合
 * 提供统一的数据访问接口，封装状态选择逻辑
 */
export const roomTypeSelectors = {
  /**
   * 获取指定服务类型的房间类型数据
   * @param {string} serviceType - 服务类型，默认为'default'
   * @returns {Function} 返回选择器函数
   */
  selectRoomTypeData: (serviceType = 'default') => (state) =>
    state.roomType[serviceType] || [],

  /**
   * 获取指定服务类型和数据类型的房间类型数据
   * @param {string} serviceType - 服务类型，默认为'default'
   * @param {string} dataType - 数据类型，如'roomTypeOption'、'roomTypeList'等
   * @returns {Function} 返回选择器函数
   */
  selectRoomTypeDataByType: (serviceType = 'default', dataType) => (state) => {
    if (!dataType) {
      return state.roomType[serviceType] || [];
    }
    const key = `${serviceType}_${dataType}`;
    return state.roomType[key] || [];
  },

  /**
   * 获取指定服务类型的查询参数
   * @param {string} serviceType - 服务类型，默认为'default'
   * @returns {Function} 返回选择器函数
   */
  selectRoomTypeQuery: (serviceType = 'default') => (state) =>
    state.roomType.query[serviceType] || {},

  /**
   * 获取全局加载状态
   * @returns {Function} 返回选择器函数
   */
  selectRoomTypeFetching: (state) => state.roomType.fetching,

  /**
   * 获取全局错误信息
   * @returns {Function} 返回选择器函数
   */
  selectRoomTypeError: (state) => state.roomType.error,

};

// 为了向后兼容，导出常用的选择器函数
export const {
  selectRoomTypeData,
  selectRoomTypeDataByType,
  selectRoomTypeQuery,
  selectRoomTypeFetching,
  selectRoomTypeError,
  selectRoomTypeGroupBy
} = roomTypeSelectors;

// ==================== 导出Reducer ====================

export default roomTypeSlice.reducer;
